import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { FiMenu, FiX } from 'react-icons/fi';

const Navbar = () => {
  const {} = useApp();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);

    window.addEventListener('scroll', onScroll, {
      passive: true,
    });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu and reset scroll on route change
  useEffect(() => {
    setMobileOpen(false);

    window.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }, [location.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleNavClick = useCallback(
    (to) => {
      setMobileOpen(false);

      if (location.pathname === to) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'instant',
        });

        navigate(to);
      }
    },
    [location.pathname, navigate]
  );

  const links = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/projects', label: 'Projects' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';

    return location.pathname.startsWith(to);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.65,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'backdrop-blur-2xl bg-black/55 border-b border-purple-900/25 shadow-2xl'
            : 'bg-transparent'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Navbar Container */}
        <div className="max-w-7xl mx-auto px-4 py-1 flex items-center justify-between">

          {/* Logo */}
          <button
            onClick={() => handleNavClick('/')}
            className="flex items-center group"
            aria-label="SitePixel home"
          >
            {/* Bigger Logo Without Restriction */}
            <img
              src="/Logo without BG.png"
              alt="SitePixel Logo"
              className="w-18 h-20 object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </button>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            {links.map((link) => (
              <button
                key={link.to}
                onClick={() => handleNavClick(link.to)}
                className={`nav-link font-body text-sm font-medium tracking-wide transition-colors duration-250 ${
                  isActive(link.to)
                    ? 'text-purple-400 active'
                    : 'text-white/65 hover:text-white'
                }`}
                aria-current={isActive(link.to) ? 'page' : undefined}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">

            <button
              onClick={() => handleNavClick('/contact')}
              className="hidden md:block btn-primary text-sm py-2 px-6"
            >
              Get Started
            </button>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-white/65 hover:text-white transition-colors duration-200 p-1"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <FiX size={22} />
              ) : (
                <FiMenu size={22} />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col pt-24 px-8 pb-12"
            style={{
              background: 'rgba(13,8,16,0.97)',
              backdropFilter: 'blur(28px)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <nav className="flex flex-col gap-1">

              {links.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: i * 0.07,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  <button
                    onClick={() => handleNavClick(link.to)}
                    className={`block w-full text-left py-5 font-display text-4xl font-normal border-b border-white/5 transition-colors duration-200 ${
                      isActive(link.to)
                        ? 'text-purple-400'
                        : 'text-white/75 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </button>
                </motion.div>
              ))}
            </nav>

            {/* Mobile CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.42 }}
              className="mt-10"
            >
              <button
                onClick={() => handleNavClick('/contact')}
                className="btn-primary inline-block text-lg px-10 py-4"
              >
                Start a Project
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;